/**
 * Import Review model — all import/review logic, free of React and of any
 * hard-coded English.
 *
 * Regional rule, inherited from Stage 1 and not re-implemented here: the
 * measurement system comes from the regional profile, never from the UI
 * locale. There is no `locale === ...` test anywhere in this file.
 *
 * Three layers stay separate throughout:
 *
 *   source     what the document said, verbatim      -> Source Details
 *   canonical  cm / kg                               -> API and storage
 *   display    the market's units                    -> Review rows and Fix
 *
 * Reasons are returned as codes, not sentences, so the Review screen can
 * localize them through the existing translation mechanism.
 */
import { usesMetricMeasurementSystem } from "../../configs/regionalProfile";
import {
  feetAndInchesToCentimeters,
  centimetersToFeetAndInches,
  poundsToKilograms,
  kilogramsToPounds,
  isHeightCentimetersInRange,
  isWeightKilogramsInRange,
} from "../testPerson/measurementUnits";
/* The market-aware resolver is used deliberately. `usesMetricMeasurements()` in
   measurementUnits ignores its argument by design (Stage 1 backward
   compatibility), so it always answers for the running build; this module needs
   to answer for an explicit market as well, which the profile resolver does.
   Both read the same regional profile, so the answer is identical for the
   active build. Neither consults the UI locale. */

import {
  normalizeExternalId,
  getExternalIdError,
} from "../../utlis/externalId";

/* ------------------------------------------------------- registration gate */

/**
 * The eight fields that gate registration. Locked. Initials count as names.
 * Optional profile data never appears here, so it can never block an import.
 * SeniorLife ID is system generated and is deliberately absent.
 */
export const REQUIRED_FIELDS = [
  "firstName",
  "lastName",
  "extId",
  "gender",
  "birthYear",
  "birthMonth",
  "height",
  "weight",
];

export const REASON = {
  FIRST_NAME_MISSING: "first_name_missing",
  LAST_NAME_MISSING: "last_name_missing",
  EXTERNAL_ID_MISSING: "external_id_missing",
  EXTERNAL_ID_INVALID: "external_id_invalid",
  EXTERNAL_ID_SPACE: "external_id_space",
  EXTERNAL_ID_CHARACTERS: "external_id_characters",
  EXTERNAL_ID_MIXED: "external_id_mixed",
  EXTERNAL_ID_DUPLICATE_FILE: "external_id_duplicate_file",
  EXTERNAL_ID_EXISTS: "external_id_exists",
  GENDER_MISSING: "gender_missing",
  BIRTH_YEAR_MISSING: "birth_year_missing",
  BIRTH_MONTH_MISSING: "birth_month_missing",
  HEIGHT_MISSING: "height_missing",
  HEIGHT_OUT_OF_RANGE: "height_out_of_range",
  WEIGHT_MISSING: "weight_missing",
  WEIGHT_OUT_OF_RANGE: "weight_out_of_range",
};

const blank = (v) => v === null || v === undefined || String(v).trim() === "";

export {
  EXTERNAL_ID_PATTERN,
  normalizeExternalId,
  isValidExternalId,
} from "../../utlis/externalId";

/* ------------------------------------------------- market-aware field sets */

/**
 * Which inputs the Fix panel shows for height and weight.
 *
 * Metric profiles get ONE height input in cm. Imperial profiles get feet and
 * inches. This drives the draft model and validation as well as the markup, so
 * a metric market never has a feet/inches draft field to fill in.
 */
export const heightFieldsFor = (market) =>
  usesMetricMeasurementSystem(market)
    ? ["heightCm"]
    : ["heightFeet", "heightInches"];

export const weightFieldFor = (market) =>
  usesMetricMeasurementSystem(market) ? "weightKg" : "weightLb";

export const editableFieldsFor = (market) => [
  "extId",
  "firstName",
  "lastName",
  "gender",
  "birthYear",
  "birthMonth",
  ...heightFieldsFor(market),
  weightFieldFor(market),
];

/** i18n key for a field's label, and the unit token it needs (if any). */
export const fieldLabelKey = (field) =>
  ({
    extId: "field_person_id",
    firstName: "field_first_name",
    lastName: "field_last_name",
    gender: "field_gender",
    birthYear: "field_birth_year",
    birthMonth: "field_birth_month",
    heightCm: "field_height_cm",
    heightFeet: "field_height_feet",
    heightInches: "field_height_inches",
    weightKg: "field_weight_kg",
    weightLb: "field_weight_lb",
  })[field];

/* -------------------------------------------------------------- validation */

/**
 * Reasons a committed row cannot be registered, as codes.
 *
 * `allRows` is the rest of the uploaded file, for the in-file duplicate check.
 * `isExternalIdTaken` reports IDs already registered in this customer account;
 * as in Add, this is an early convenience check and the server remains
 * authoritative immediately before creation.
 */
export function rowReasons(row, allRows = [], isExternalIdTaken = () => false) {
  const out = [];
  if (blank(row.firstName)) out.push(REASON.FIRST_NAME_MISSING);
  if (blank(row.lastName)) out.push(REASON.LAST_NAME_MISSING);

  const ext = normalizeExternalId(row.extId);
  const idError = getExternalIdError(ext);
  if (idError) {
    out.push(idError);
  } else {
    const key = ext.toLowerCase();
    const first = allRows.find(
      (r) => normalizeExternalId(r.extId).toLowerCase() === key,
    );
    if (first && first._id !== row._id)
      out.push(REASON.EXTERNAL_ID_DUPLICATE_FILE);
    else if (isExternalIdTaken(ext)) out.push(REASON.EXTERNAL_ID_EXISTS);
  }

  if (blank(row.gender)) out.push(REASON.GENDER_MISSING);
  if (blank(row.birthYear)) out.push(REASON.BIRTH_YEAR_MISSING);
  if (blank(row.birthMonth)) out.push(REASON.BIRTH_MONTH_MISSING);

  if (blank(row.heightCm)) out.push(REASON.HEIGHT_MISSING);
  else if (!isHeightCentimetersInRange(Number(row.heightCm)))
    out.push(REASON.HEIGHT_OUT_OF_RANGE);

  if (blank(row.weightKg)) out.push(REASON.WEIGHT_MISSING);
  else if (!isWeightKilogramsInRange(Number(row.weightKg)))
    out.push(REASON.WEIGHT_OUT_OF_RANGE);

  return out;
}

export const isReady = (row, allRows, isExternalIdTaken) =>
  rowReasons(row, allRows, isExternalIdTaken).length === 0;

/**
 * Which inputs Fix should show for a set of reasons, in the market's units.
 * A metric market fixing a height reason gets one cm field, not two.
 */
export function fieldsForReasons(reasons, market) {
  const fields = [];
  const add = (f) => {
    if (!fields.includes(f)) fields.push(f);
  };
  for (const r of reasons) {
    switch (r) {
      case REASON.FIRST_NAME_MISSING:
        add("firstName");
        break;
      case REASON.LAST_NAME_MISSING:
        add("lastName");
        break;
      case REASON.EXTERNAL_ID_MISSING:
      case REASON.EXTERNAL_ID_INVALID:
      case REASON.EXTERNAL_ID_SPACE:
      case REASON.EXTERNAL_ID_CHARACTERS:
      case REASON.EXTERNAL_ID_MIXED:
      case REASON.EXTERNAL_ID_DUPLICATE_FILE:
      case REASON.EXTERNAL_ID_EXISTS:
        add("extId");
        break;
      case REASON.GENDER_MISSING:
        add("gender");
        break;
      case REASON.BIRTH_YEAR_MISSING:
        add("birthYear");
        break;
      case REASON.BIRTH_MONTH_MISSING:
        add("birthMonth");
        break;
      case REASON.HEIGHT_MISSING:
      case REASON.HEIGHT_OUT_OF_RANGE:
        heightFieldsFor(market).forEach(add);
        break;
      case REASON.WEIGHT_MISSING:
      case REASON.WEIGHT_OUT_OF_RANGE:
        add(weightFieldFor(market));
        break;
      default:
        break;
    }
  }
  // Keep the canonical field order rather than reason order.
  return editableFieldsFor(market).filter((f) => fields.includes(f));
}

/* ------------------------------------------------------------- conversions */

const round1 = (n) => Math.round(n * 10) / 10;

/** Canonical row -> the market's editable draft values. */
export function toDraft(row, market) {
  const draft = {
    extId: row.extId ?? "",
    firstName: row.firstName ?? "",
    lastName: row.lastName ?? "",
    gender: row.gender ?? "",
    birthYear: row.birthYear ?? "",
    birthMonth: row.birthMonth ?? "",
  };
  if (usesMetricMeasurementSystem(market)) {
    draft.heightCm = blank(row.heightCm)
      ? ""
      : String(round1(Number(row.heightCm)));
    draft.weightKg = blank(row.weightKg)
      ? ""
      : String(round1(Number(row.weightKg)));
  } else {
    if (blank(row.heightCm)) {
      draft.heightFeet = "";
      draft.heightInches = "";
    } else {
      const { feet, inches } = centimetersToFeetAndInches(Number(row.heightCm));
      draft.heightFeet = String(feet);
      draft.heightInches = String(inches);
    }
    draft.weightLb = blank(row.weightKg)
      ? ""
      : String(round1(Number(kilogramsToPounds(Number(row.weightKg)))));
  }
  return draft;
}

/**
 * Draft -> canonical cm / kg, using the Stage 1 conversion utilities. Both
 * measurement systems land in the same canonical shape, so nothing downstream
 * needs to know which market produced the row.
 */
export function draftToCanonical(draft, market) {
  const out = {
    extId: normalizeExternalId(draft.extId),
    firstName: String(draft.firstName ?? "").trim(),
    lastName: String(draft.lastName ?? "").trim(),
    gender: draft.gender ?? "",
    birthYear: draft.birthYear ?? "",
    birthMonth: draft.birthMonth ?? "",
  };
  if (usesMetricMeasurementSystem(market)) {
    out.heightCm = blank(draft.heightCm) ? "" : Number(draft.heightCm);
    out.weightKg = blank(draft.weightKg) ? "" : Number(draft.weightKg);
  } else {
    out.heightCm =
      blank(draft.heightFeet) || blank(draft.heightInches)
        ? ""
        : Number(
            feetAndInchesToCentimeters(
              Number(draft.heightFeet),
              Number(draft.heightInches),
            ),
          );
    out.weightKg = blank(draft.weightLb)
      ? ""
      : Number(poundsToKilograms(Number(draft.weightLb)));
  }
  return out;
}

/**
 * Map a committed Review row onto the SAME `step1` / `step2` shape Add Person
 * feeds to `toPatientPayload`, so an imported person goes through the identical
 * canonical person contract: `{ data: [{ uniqueId, value }] }` with height in
 * cm and weight in kg.
 *
 * v100 removed an earlier flat snake_case payload helper that lived here: it
 * did not match the contract envelope and was wired to nothing. Nothing should
 * reconstruct a person payload by hand — always go through `toPatientPayload`.
 *
 * Only the eight registration fields are sent. Import collects no optional
 * profile data, so nothing optional is invented here, and Blood Pressure
 * Profile is a separate endpoint that import does not touch.
 */
export function toPersonCreateInput(row) {
  return {
    step1: {
      firstname: row.firstName ?? "",
      lastname: row.lastName ?? "",
      testPersonId: normalizeExternalId(row.extId),
      gender: row.gender ?? "",
      birthYear: row.birthYear ?? "",
      birthMonth: row.birthMonth ?? "",
    },
    step2: {
      height: blank(row.heightCm) ? "" : Number(row.heightCm), // cm, canonical
      weight: blank(row.weightKg) ? "" : Number(row.weightKg), // kg, canonical
      zipcode: "",
      email: "",
      phone: "",
      exercises: "",
    },
  };
}

/* ---------------------------------------------------------------- display */

/**
 * The compact height/weight text on a Review row, in the market's units.
 * Returns tokens rather than a sentence so the caller can localize the units.
 */
export function sizeParts(row, market) {
  if (usesMetricMeasurementSystem(market)) {
    return {
      metric: true,
      heightCm: blank(row.heightCm) ? null : round1(Number(row.heightCm)),
      weightKg: blank(row.weightKg) ? null : round1(Number(row.weightKg)),
    };
  }
  const h = blank(row.heightCm)
    ? null
    : centimetersToFeetAndInches(Number(row.heightCm));
  return {
    metric: false,
    feet: h ? h.feet : null,
    inches: h ? h.inches : null,
    weightLb: blank(row.weightKg)
      ? null
      : round1(Number(kilogramsToPounds(Number(row.weightKg)))),
  };
}

/**
 * Source Details, exactly as the document supplied them. Never reformatted
 * into the customer's display units and never derived from the canonical
 * values, so a document that said 172 cm keeps saying 172 cm in the US region.
 */
export const sourceDetails = (row) => ({ ...(row.source || {}) });

/* --------------------------------------------------- sample document map */

/** Which sample document the Upload screen offers for download. This selects a
    downloadable example only; it has no role in extraction. */
export const SAMPLE_DOC_FOR = {
  home_care: "client",
  senior_living: "resident",
  rehab: "patient",
  primary_care: "patient",
};

/* ------------------------------------------------------------ extraction */

/* There is deliberately no sample fixture in production. Extraction comes from
   the file the user chose, via importExtractionApi. A built-in set of people
   here would make it possible to show sample rows for a real upload, which is
   the defect v101 removed. The fixture lives in qa/fixtures for tests and in
   standalone-verification for visual checking. */

/** Ready / needs-attention split, computed from committed rows only. */
export function partitionRows(rows, isExternalIdTaken) {
  const ready = [],
    attention = [];
  for (const row of rows) {
    const reasons = rowReasons(row, rows, isExternalIdTaken);
    (reasons.length ? attention : ready).push({ row, reasons });
  }
  return { ready, attention };
}
